import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getVods } from '../api/rttsClient';
import { LSC_LOGO_PATH } from '../../lsc/constants';
import {
  PRICE_DISPLAY,
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  STUDIO_PRICING,
  STUDIO_VALUE_PROP,
} from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import StudioExpectationPanel from '../components/StudioExpectationPanel';
import StudioHomeDashboard from '../components/StudioHomeDashboard';
import StudioHostLink from '../components/StudioHostLink';
import StudioLandingReel from '../components/StudioLandingReel';
import { studioLscHomeUrl } from '../studioLinks';
import { isGameEligibleVod } from '../utils/vodEligibility';
import studioLogo from '../assets/scoutlytics-studio-horizontal.svg';

const HOW_STEPS = [
  {
    title: STUDIO_COPY.landingHowConnectTitle,
    body: STUDIO_COPY.landingHowConnectBody,
  },
  {
    title: STUDIO_COPY.landingHowPickTitle,
    body: STUDIO_COPY.landingHowPickBody,
  },
  {
    title: STUDIO_COPY.landingHowRunTitle,
    body: STUDIO_COPY.landingHowRunBody,
  },
  {
    title: STUDIO_COPY.landingHowReelTitle,
    body: STUDIO_COPY.landingHowReelBody,
  },
] as const;

const StudioMarketingLanding: React.FC = () => {
  const lscHref = studioLscHomeUrl();
  const paidLine = STUDIO_COPY.landingPricingPaidTemplate
    .replace('{price}', STUDIO_PRICING.monthly.priceLabel)
    .replace('{n}', String(STUDIO_PRICING.monthly.reels));
  const heroPrice = STUDIO_COPY.landingHeroPriceTemplate
    .replace('{price}', PRICE_DISPLAY)
    .replace('{free}', STUDIO_PRICING.free.label);

  return (
    <div className="studio-page" data-testid="studio-landing">
      <section className="studio-landing-hero" data-testid="studio-landing-hero">
        <div
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
          aria-hidden
        >
          <div className="absolute top-8 left-0 h-64 w-[min(100%,36rem)] rounded-full bg-[rgba(139,92,246,0.12)] blur-3xl" />
        </div>
        <div className="studio-landing-hero-copy">
          <img
            src={studioLogo}
            alt="Scoutlytics Studio"
            className="studio-fade-up studio-hero-logo"
            data-testid="studio-landing-brand"
          />

          <h1 className="studio-hero-headline studio-fade-up">
            {STUDIO_COPY.landingHeadline}
          </h1>
          <p className="studio-fade-up-delay studio-prose text-base leading-6 text-[var(--studio-muted)]">
            {STUDIO_COPY.landingSubhead}
          </p>
          <p
            className="studio-fade-up-delay studio-prose text-base leading-6 text-[var(--studio-muted)]"
            data-testid="studio-landing-value-prop"
          >
            {STUDIO_VALUE_PROP}
          </p>

          <div className="studio-fade-up-delay flex flex-col items-start gap-3 pt-2">
            <Link
              to="/studio/connect"
              className="studio-btn-primary no-underline"
              data-testid="studio-landing-cta"
            >
              {STUDIO_COPY.landingCta}
            </Link>
            <p
              className="text-sm text-[var(--studio-muted)]"
              data-testid="studio-landing-price"
            >
              {heroPrice}
            </p>
            <Link
              to="/studio/upgrade"
              className="studio-btn-ghost no-underline"
              data-testid="studio-landing-see-plans"
            >
              {STUDIO_COPY.seePlansCta}
            </Link>
          </div>
        </div>

        <div className="studio-landing-hero-media">
          <StudioLandingReel />
        </div>
      </section>

      <div
        className="studio-landing-expandable"
        data-testid="studio-landing-expandable"
      >
        <section data-testid="studio-landing-expectation">
          <StudioExpectationPanel />
        </section>

        {/*
          Claims here describe the clip-tool category and are grounded in those
          products' own public marketing language (reaction/engagement signals;
          "rearranges" long video into short-form). Do not name competitors, and
          do not sharpen these without a citable quote. Verified 2026-09-06.
        */}
        <section
          className="studio-landing-section"
          data-testid="studio-landing-difference"
        >
          <h2 className="studio-page-heading">{STUDIO_COPY.landingDiffHeading}</h2>
          <p className="studio-landing-body">{STUDIO_COPY.landingDiffSub}</p>
          <ul className="studio-landing-diff-list">
            <li className="studio-card">
              <p className="studio-landing-diff-title">
                {STUDIO_COPY.landingDiffReactionTitle}
              </p>
              <p className="studio-landing-card-body">
                {STUDIO_COPY.landingDiffReactionBody}
              </p>
            </li>
            <li className="studio-card">
              <p className="studio-landing-diff-title">
                {STUDIO_COPY.landingDiffOrderTitle}
              </p>
              <p className="studio-landing-card-body">
                {STUDIO_COPY.landingDiffOrderBody}
              </p>
            </li>
            <li className="studio-card">
              <p className="studio-landing-diff-title">
                {STUDIO_COPY.landingDiffFootballTitle}
              </p>
              <p className="studio-landing-card-body">
                {STUDIO_COPY.landingDiffFootballBody}
              </p>
            </li>
          </ul>
        </section>

        <section
          className="studio-landing-section"
          data-testid="studio-landing-how"
        >
          <h2 className="studio-page-heading">{STUDIO_COPY.landingHowHeading}</h2>
          <ol className="studio-landing-how-list">
            {HOW_STEPS.map((step, index) => (
              <li key={step.title} className="studio-card">
                <span className="studio-landing-how-index" aria-hidden="true">
                  {index + 1}
                </span>
                <div>
                  <p className="studio-landing-diff-title">{step.title}</p>
                  <p className="studio-landing-card-body">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="studio-landing-section studio-landing-limits"
          data-testid="studio-landing-limits"
        >
          <h2 className="studio-page-heading">
            {STUDIO_COPY.landingLimitsHeading}
          </h2>
          <div className="studio-landing-limits-grid">
            <p className="studio-card">{STUDIO_COPY.landingLimitEta}</p>
            <p className="studio-card">{STUDIO_COPY.landingLimitRetention}</p>
            <p className="studio-card">{STUDIO_COPY.landingLimitTitle}</p>
          </div>
        </section>

        <section
          className="studio-landing-section"
          data-testid="studio-landing-pricing"
        >
          <h2 className="studio-page-heading">
            {STUDIO_COPY.landingPricingHeading}
          </h2>
          <div className="studio-panel space-y-3">
            <p className="studio-landing-body">{STUDIO_PRICING.free.label}</p>
            <p className="studio-landing-body">{paidLine}</p>
            <p className="studio-fine-print">{STUDIO_COPY.landingSeePlansHint}</p>
            <Link
              to="/studio/upgrade"
              className="studio-btn-ghost no-underline inline-flex"
            >
              {STUDIO_COPY.seePlansCta}
            </Link>
          </div>
        </section>

        <section
          className="studio-landing-lsc-feature"
          data-testid="studio-landing-lsc-feature"
        >
          <img
            src={LSC_LOGO_PATH}
            alt=""
            className="studio-landing-lsc-logo"
          />
          <div>
            <p className="studio-landing-diff-title">
              {STUDIO_COPY.landingLscHeading}
            </p>
            <p className="studio-landing-card-body">{STUDIO_COPY.landingLscBody}</p>
            <StudioHostLink
              href={lscHref}
              className="studio-landing-lsc-cta no-underline"
              testId="studio-landing-lsc-cta"
            >
              {STUDIO_COPY.landingLscCta}
            </StudioHostLink>
          </div>
        </section>
      </div>
    </div>
  );
};

type AuthedView = 'pending' | 'dashboard' | 'landing';

type StudioLandingPageProps = {
  /** Logo (and empty-VOD Home) target — never swap to the dashboard. */
  forceMarketing?: boolean;
};

/**
 * `/studio`: signed-out marketing; signed-in with VODs → dashboard;
 * signed-in with zero eligible Twitch VODs → marketing.
 * `/studio/landing`: always marketing (logo target).
 */
const StudioLandingPage: React.FC<StudioLandingPageProps> = ({
  forceMarketing = false,
}) => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.base);
  const { status, isLoggedOut, resolveTimedOut } = useStudioAuth();
  const [authedView, setAuthedView] = useState<AuthedView>(
    forceMarketing ? 'landing' : 'pending'
  );

  useEffect(() => {
    if (forceMarketing) {
      setAuthedView('landing');
      return;
    }
    if (isLoggedOut || status === 'loading') {
      setAuthedView('pending');
      return;
    }
    let cancelled = false;
    void getVods()
      .then((vods) => {
        if (cancelled) return;
        setAuthedView(vods.some(isGameEligibleVod) ? 'dashboard' : 'landing');
      })
      .catch(() => {
        if (!cancelled) setAuthedView('dashboard');
      });
    return () => {
      cancelled = true;
    };
  }, [forceMarketing, isLoggedOut, status]);

  if (status === 'loading' && !resolveTimedOut && !forceMarketing) {
    return (
      <div className="flex justify-center py-16">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (forceMarketing) {
    return <StudioMarketingLanding />;
  }

  if (!isLoggedOut && status !== 'loading') {
    if (authedView === 'pending') {
      return (
        <div className="flex justify-center py-16" data-testid="studio-home-auth-loading">
          <div className="studio-spinner" />
        </div>
      );
    }
    if (authedView === 'dashboard') {
      return <StudioHomeDashboard />;
    }
    return <StudioMarketingLanding />;
  }

  return <StudioMarketingLanding />;
};

export default StudioLandingPage;
